import { apiAbortOptions, useApi } from '@/api/useApi.ts';
import { createChatStreamParser } from '@/api/chatBot/chatStreamParser.ts';
import { ChatStreamEventTypeEnum } from '@/enums/chatBot/ChatStreamEnums.ts';
import type { IChatStreamRequest } from '@/interfaces/chatBot/IChatStreamRequest.ts';
import type { ChatStreamEvent } from '@/interfaces/chatBot/IChatStreamEvent.ts';
import type {
    IChatConversationSummary,
    IStoredChatConversation,
} from '@/interfaces/chatBot/IStoredChatConversation.ts';
import type { ChatResponder } from '@/types/chatBot/ChatResponder.ts';
import type { ChatConversationHistoryClient } from '@/types/chatBot/ChatConversationHistoryClient.ts';
import type { IAiUsageQuery, IAiUsageQueryResult } from '@/interfaces/chatBot/IAiUsageQuery.ts';
import type { ICompanyBudgetSummary } from '@/interfaces/chatBot/ICompanyBudgetSummary.ts';

const apiCall = useApi('chatBot');

export async function getCompanyBudgetSummary (organisationId: string, signal?: AbortSignal): Promise<ICompanyBudgetSummary> {
    return (await apiCall.get<ICompanyBudgetSummary>({
        endpoint: `/chatbot/admin/companies/${encodeURIComponent(organisationId)}/budget`,
        ...apiAbortOptions(signal),
    })).data;
}

export async function refreshChatBotUserAccess (): Promise<void> {
    await apiCall.get({ endpoint: '/chatbot/refresh-user-access' });
}

export async function clearChatBotCompanyBudgetCache (organisationId: string): Promise<void> {
    await apiCall.delete<Record<string, never>, void>({
        endpoint: `/chatbot/admin/companies/${encodeURIComponent(organisationId)}/budget/cache`,
        payload: {},
    });
}

export const chatBotResponder: ChatResponder = async (
    question,
    requestId,
    requestedConversationId,
    mode,
    onText,
    setConversationId,
    signal,
) => {
    let terminalEvent: ChatStreamEventTypeEnum.Complete | ChatStreamEventTypeEnum.Error | undefined;
    let streamError = '';
    let conversationId = requestedConversationId;

    function handleEvent (event: ChatStreamEvent): void {
        if (signal.aborted) return;
        if (terminalEvent) throw new Error('ChatBot stream continued after a terminal event');
        if (conversationId && event.conversationId !== conversationId) {
            throw new Error('ChatBot stream changed conversation ID');
        }
        if (!conversationId) {
            conversationId = event.conversationId;
            setConversationId(conversationId);
        }

        switch (event.type) {
            case ChatStreamEventTypeEnum.Status:
                break;
            case ChatStreamEventTypeEnum.TextDelta:
                onText(event.delta);
                break;
            case ChatStreamEventTypeEnum.Error:
                terminalEvent = ChatStreamEventTypeEnum.Error;
                streamError = event.message;
                break;
            case ChatStreamEventTypeEnum.Complete:
                terminalEvent = ChatStreamEventTypeEnum.Complete;
                break;
        }
    }

    const parser = createChatStreamParser(handleEvent);
    await apiCall.postStream<IChatStreamRequest>({
        endpoint: '/chatbot/stream',
        payload: {
            message: question,
            requestId,
            mode,
            timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(requestedConversationId ? { conversationId: requestedConversationId } : {}),
        },
        ...apiAbortOptions(signal),
    }, parser.push);
    parser.finish();

    if (terminalEvent === ChatStreamEventTypeEnum.Error) throw new Error(streamError);
    if (terminalEvent !== ChatStreamEventTypeEnum.Complete) throw new Error('ChatBot stream ended before completion');
    return [];
};

async function listConversations (signal?: AbortSignal): Promise<Array<IChatConversationSummary>> {
    return (await apiCall.get<Array<IChatConversationSummary>>({
        endpoint: '/chatbot/conversations',
        ...apiAbortOptions(signal),
    })).data;
}

async function getConversation (
    conversationId: string,
    signal?: AbortSignal,
): Promise<IStoredChatConversation> {
    return (await apiCall.get<IStoredChatConversation>({
        endpoint: `/chatbot/conversations/${encodeURIComponent(conversationId)}`,
        ...apiAbortOptions(signal),
    })).data;
}

async function deleteConversation (conversationId: string): Promise<void> {
    await apiCall.delete<Record<string, never>, void>({
        endpoint: `/chatbot/conversations/${encodeURIComponent(conversationId)}`,
        payload: {},
    });
}

export const chatBotConversationApi: ChatConversationHistoryClient = {
    list: listConversations,
    get: getConversation,
    delete: deleteConversation,
};

export async function getAiUsage (query: IAiUsageQuery, signal?: AbortSignal): Promise<IAiUsageQueryResult> {
    const search = new URLSearchParams();
    Object.entries(query).forEach(([name, value]) => {
        if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) return;
        search.set(name, Array.isArray(value) ? value.join(',') : value);
    });
    return (await apiCall.get<IAiUsageQueryResult>({
        endpoint: `/chatbot/admin/ai-usage?${search.toString()}`,
        ...apiAbortOptions(signal),
    })).data;
}
